import { useState } from "react";
import { Button } from "./button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Styled replacement for window.confirm so high-stakes actions match the app's
 * visual language and are keyboard and screen-reader operable.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.ReactElement | null {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{description}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="confirm-dialog-cancel">
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            size="sm"
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-dialog-confirm"
          >
            {loading ? "Working..." : confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface UseConfirmResult {
  confirm: (opts: ConfirmRequest) => Promise<boolean>;
  dialog: React.ReactElement | null;
}

interface ConfirmRequest {
  title: string;
  description: string;
  confirmLabel?: string;
  destructive?: boolean;
}

/**
 * Promise-based confirm hook: `const ok = await confirm({...})`, render `dialog`
 * once in the component. Replaces the blocking window.confirm pattern.
 */
export function useConfirm(): UseConfirmResult {
  const [state, setState] = useState<{ req: ConfirmRequest; resolve: (v: boolean) => void } | null>(null);

  const confirm = (req: ConfirmRequest): Promise<boolean> =>
    new Promise((resolve) => {
      setState({ req, resolve });
    });

  const close = (value: boolean): void => {
    state?.resolve(value);
    setState(null);
  };

  const dialog = state ? (
    <ConfirmDialog
      open
      title={state.req.title}
      description={state.req.description}
      confirmLabel={state.req.confirmLabel}
      destructive={state.req.destructive}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  ) : null;

  return { confirm, dialog };
}
